<template>
  <div class="saved-prices-page">
    <h1>Saved Prices</h1>
    <p>Esta página mostra os preços guardados para o cliente.</p>

    <!-- Campo de pesquisa por SKU ou Nome -->
    <div class="search-bar">
      <input
        v-model="searchQuery"
        type="text"
        placeholder="Pesquisar por SKU ou Nome..."
        class="search-input"
      />
    </div>

    <!-- Spinner global enquanto carrega os dados -->
    <div v-if="isFetching" class="global-loading">
      <div class="spinner"></div>
      <span>Carregando preços...</span>
    </div>

    <!-- Conteúdo dos cards só aparece após o fetch completar -->
    <div v-else class="cards-container">
      <div class="price-card" v-for="item in filteredPrices" :key="item.id">
        <!-- Indicador de status fixo no canto superior direito -->
        <span
          class="status-badge"
          :class="{ active: isActive(item), inactive: !isActive(item) }"
        >
          {{ isActive(item) ? 'Ativo' : 'Inativo' }}
        </span>

        <!-- Parte superior com imagem, nome e SKU -->
        <div class="info-top">
          <img
            :src="item.product_info.img"
            alt="Product Image"
            class="product-img"
          />
          <h2 class="product-name">{{ item.product_info.name }}</h2>
          <p class="product-sku">
            <strong>SKU:</strong>
            <span v-if="Array.isArray(item.product_info.sku)">
              {{ item.product_info.sku.join(', ') }}
            </span>
            <span v-else>
              {{ item.product_info.sku }}
            </span>
          </p>
        </div>

        <!-- Parte inferior (inputs + botões) fixa na base do card -->
        <div class="footer">
          <!-- Sub-card de inputs para compactar e manter posição fixa -->
          <div class="inputs-card">
            <div class="field-group">
              <label><strong>Preço Unitário:</strong></label>
              <input
                v-model="item.price_uni"
                type="number"
                step="0.01"
                class="input-field"
                :disabled="item.isUpdating || item.isDeleting"
              />
            </div>
            <div class="field-group">
              <label><strong>Exchange:</strong></label>
              <input
                v-model="item.exchange"
                type="number"
                step="0.01"
                class="input-field"
                :disabled="item.isUpdating || item.isDeleting"
              />
            </div>
            <div class="field-group">
              <label><strong>Valid Until:</strong></label>
              <input
                v-model="item.valid_until_date"
                type="date"
                class="input-field"
                :disabled="item.isUpdating || item.isDeleting"
              />
            </div>
          </div>

          <!-- Botões de ação: sempre abaixo dos inputs -->
          <div class="buttons">
            <button
              @click="onUpdate(item)"
              class="btn btn-update"
              :disabled="item.isUpdating || item.isDeleting"
            >
              <span v-if="item.isUpdating" class="btn-spinner"></span>
              <span v-else>Atualizar</span>
            </button>
            <button
              @click="onDelete(item)"
              class="btn btn-delete"
              :disabled="item.isUpdating || item.isDeleting"
            >
              <span v-if="item.isDeleting" class="btn-spinner"></span>
              <span v-else>Apagar</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Mensagem quando não há resultados na pesquisa -->
      <div v-if="filteredPrices.length === 0 && !isFetching" class="no-results">
        Nenhum item encontrado para "<strong>{{ searchQuery }}</strong
        >"
      </div>
    </div>
  </div>
</template>

<script>
import Swal from 'sweetalert2';
import axios from 'axios';

export default {
  data() {
    return {
      savedPrices: [], // array com os preços vindos da API
      isFetching: false, // controla spinner global ao buscar dados
      searchQuery: '', // texto da pesquisa
    };
  },
  computed: {
    // Retorna somente os itens cujo nome ou SKU contêm a searchQuery (case-insensitive)
    filteredPrices() {
      if (!this.searchQuery.trim()) {
        return this.savedPrices;
      }
      const query = this.searchQuery.trim().toLowerCase();
      return this.savedPrices.filter((item) => {
        // Verifica nome
        const nameMatch = item.product_info.name.toLowerCase().includes(query);

        // Verifica SKU: pode ser string ou array
        let skuMatch = false;
        if (Array.isArray(item.product_info.sku)) {
          skuMatch = item.product_info.sku.some((s) =>
            s.toLowerCase().includes(query)
          );
        } else if (typeof item.product_info.sku === 'string') {
          skuMatch = item.product_info.sku.toLowerCase().includes(query);
        }

        return nameMatch || skuMatch;
      });
    },
  },
  methods: {
    // Busca preços guardados na API e inicializa valid_until_date
    async fetchSavedPrices() {
      this.isFetching = true;
      try {
        const { data, status } = await axios.get(
          `${this.$store.state.apiUrl}clientRequest/autoPrice/1`,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${this.$store.state.token}`,
            },
            validateStatus: (s) => s >= 200 && s < 500,
          }
        );

        if (status >= 200 && status < 300) {
          if (data.status === 'success') {
            // Adiciona flags de loading e valid_until_date no formato YYYY-MM-DD
            this.savedPrices = data.data.map((item) => ({
              ...item,
              valid_until_date: this.isoToYMD(item.valid_until),
              isUpdating: false,
              isDeleting: false,
            }));
          } else {
            Swal.fire(
              'Erro',
              data.message || 'Falha ao buscar preços.',
              'error'
            );
          }
        } else {
          Swal.fire('Erro', data.message || 'Falha ao buscar preços.', 'error');
        }
      } catch (err) {
        console.error(err);
        Swal.fire(
          'Erro',
          'Ocorreu um erro de rede ao buscar os preços.',
          'error'
        );
      } finally {
        this.isFetching = false;
      }
    },

    // Converte ISO para YYYY-MM-DD
    isoToYMD(iso) {
      const date = new Date(iso);
      const pad = (n) => String(n).padStart(2, '0');
      const year = date.getFullYear();
      const month = pad(date.getMonth() + 1);
      const day = pad(date.getDate());
      return `${year}-${month}-${day}`;
    },

    // Converte YYYY-MM-DD para ISO UTC (meia-noite)
    ymdToIso(ymd) {
      const [year, month, day] = ymd.split('-').map(Number);
      const date = new Date(Date.UTC(year, month - 1, day, 0, 0, 0));
      return date.toISOString();
    },

    // Verifica se o preço está ativo comparando a data com hoje
    isActive(item) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const validDate = new Date(item.valid_until_date);
      return validDate.getTime() >= today.getTime();
    },

    // Ao clicar em “Atualizar”: chama a API com os valores atuais dos inputs
    async onUpdate(item) {
      const result = await Swal.fire({
        title: 'Tem a certeza?',
        text: `Deseja atualizar o preço para ${item.product_info.name}?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sim, atualizar!',
        cancelButtonText: 'Cancelar',
      });

      if (!result.isConfirmed) {
        return;
      }

      this.$set(item, 'isUpdating', true);

      try {
        const { data, status } = await axios.patch(
          `${this.$store.state.apiUrl}clientRequest/autoPrice/1`,
          {
            id: item.id,
            price_uni: item.price_uni,
            exchange: item.exchange,
            valid_until: this.ymdToIso(item.valid_until_date),
          },
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${this.$store.state.token}`,
            },
            validateStatus: (s) => s >= 200 && s < 500,
          }
        );

        if (status >= 200 && status < 300) {
          if (data.status === 'success') {
            Swal.fire('Sucesso', 'Preço atualizado com sucesso.', 'success');
            item.valid_until = this.ymdToIso(item.valid_until_date);
          } else {
            Swal.fire(
              'Erro',
              data.message || 'Falha ao atualizar preço.',
              'error'
            );
          }
        } else {
          Swal.fire(
            'Erro',
            data.message || 'Falha ao atualizar preço.',
            'error'
          );
        }
      } catch (error) {
        console.error('Erro ao atualizar preço:', error);
        Swal.fire('Erro', 'Ocorreu um erro ao atualizar o preço.', 'error');
      } finally {
        this.$set(item, 'isUpdating', false);
      }
    },

    // Ao clicar em “Apagar”: chama a API e remove item
    async onDelete(item) {
      const result = await Swal.fire({
        title: 'Tem a certeza?',
        text: `Deseja apagar o preço para ${item.product_info.name}?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sim, apagar!',
        cancelButtonText: 'Cancelar',
      });

      if (!result.isConfirmed) {
        return;
      }

      this.$set(item, 'isDeleting', true);

      try {
        const { data, status } = await axios.delete(
          `${this.$store.state.apiUrl}clientRequest/autoPrice/1`,
          {
            data: { id: item.id },
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${this.$store.state.token}`,
            },
            validateStatus: (s) => s >= 200 && s < 500,
          }
        );

        if (status >= 200 && status < 300) {
          if (data.status === 'success') {
            Swal.fire('Sucesso', 'Preço apagado com sucesso.', 'success');
            await this.fetchSavedPrices();
          } else {
            Swal.fire(
              'Erro',
              data.message || 'Falha ao apagar preço.',
              'error'
            );
          }
        } else {
          Swal.fire('Erro', data.message || 'Falha ao apagar preço.', 'error');
        }
      } catch (error) {
        console.error('Erro ao apagar preço:', error);
        Swal.fire('Erro', 'Ocorreu um erro ao apagar o preço.', 'error');
      } finally {
        this.$set(item, 'isDeleting', false);
      }
    },
  },

  async created() {
    await this.fetchSavedPrices();
  },
};
</script>

<style scoped>
.saved-prices-page {
  padding: 12px;
  font-family: Arial, sans-serif;
  text-align: center;
}

.saved-prices-page h1 {
  font-size: 1.6rem;
  margin-bottom: 4px;
}

.saved-prices-page p {
  font-size: 0.9rem;
  color: #555;
  margin-bottom: 12px;
}

/* Estilo do campo de pesquisa */
.search-bar {
  margin-bottom: 12px;
  text-align: center;
}
.search-input {
  width: 80%;
  max-width: 300px;
  padding: 6px 10px;
  font-size: 0.9rem;
  border: 1px solid #ccc;
  border-radius: 6px;
  box-sizing: border-box;
}

/* Grid responsivo de cards, com cards estreitos */
.cards-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 8px;
}

/* Cada card principal, compacto */
.price-card {
  position: relative; /* necessário para posicionar badge */
  background-color: #fff;
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 6px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  font-size: 0.85rem;
}

.price-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

/* Parte superior (imagem, nome, SKU) */
.info-top {
  text-align: center;
}

.product-img {
  width: 100%;
  max-width: 100px;
  height: auto;
  object-fit: contain;
  border-radius: 6px;
  margin: 0 auto 4px;
}

.product-name {
  font-size: 0.95rem;
  font-weight: bold;
  margin: 2px 0;
  color: #333;
}

.product-sku {
  font-size: 0.8rem;
  margin: 2px 0 6px;
  color: #444;
}

/* Indicador de status (badge) fixo no canto superior direito */
.status-badge {
  position: absolute;
  top: 6px;
  right: 6px;
  display: inline-block;
  padding: 2px 6px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: bold;
}
.status-badge.active {
  background-color: #4caf50;
  color: white;
}
.status-badge.inactive {
  background-color: #f44336;
  color: white;
}

/* Sub-card que contém os inputs, com fundo diferenciado */
.inputs-card {
  background-color: #fafafa;
  border: 1px solid #eee;
  border-radius: 6px;
  padding: 4px;
  margin-bottom: 4px;
}

.field-group {
  width: 100%;
  margin: 2px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.field-group label {
  font-size: 0.8rem;
  color: #444;
  margin-bottom: 2px;
}

.input-field {
  width: 75%;
  padding: 2px 4px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 0.85rem;
  box-sizing: border-box;
}

/* Container dos botões na base do card */
.buttons {
  display: flex;
  justify-content: center;
  gap: 4px;
  border-top: 1px solid #eee;
  padding-top: 4px;
}

.btn {
  padding: 4px 8px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.8rem;
  transition: opacity 0.2s ease;
  position: relative;
}

.btn:hover {
  opacity: 0.85;
}

/* Ícone/spinner dentro do botão */
.btn-spinner {
  display: inline-block;
  width: 12px;
  height: 12px;
  border: 2px solid rgba(255, 255, 255, 0.6);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
  vertical-align: middle;
}

/* Botões específicos */
.btn-update {
  background-color: #4caf50;
  color: #fff;
}

.btn-delete {
  background-color: #f44336;
  color: #fff;
}

/* Mensagem de "nenhum resultado" */
.no-results {
  grid-column: 1 / -1;
  text-align: center;
  font-size: 0.9rem;
  color: #777;
  margin-top: 12px;
}

/* Spinner global */
.global-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 20px;
}

.spinner {
  width: 24px;
  height: 24px;
  border: 3px solid rgba(0, 0, 0, 0.2);
  border-top-color: #333;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-bottom: 8px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
